import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { Video, VideoComment } from '../../types/video';
import * as api from '../../services/api';
import { RootState } from '../index';

interface VideoState {
  trendingVideos: Video[];
  currentVideo: Video | null;
  comments: Record<string, VideoComment[]>;
  commentsLoading: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: VideoState = {
  trendingVideos: [],
  currentVideo: null,
  comments: {},
  commentsLoading: false,
  loading: false,
  error: null
};

// Async thunks for API calls
export const fetchTrendingVideos = createAsyncThunk(
  'videos/fetchTrending',
  async (_, { rejectWithValue }) => {
    try {
      return await api.fetchTrendingVideos();
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const likeVideo = createAsyncThunk(
  'videos/likeVideo',
  async (videoId: string, { rejectWithValue }) => {
    try {
      // In a real app, you would get the userId from auth state
      const userId = 'current-user-id';
      await api.likeVideo(videoId, userId);
      return videoId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const unlikeVideo = createAsyncThunk(
  'videos/unlikeVideo',
  async (videoId: string, { rejectWithValue }) => {
    try {
      // In a real app, you would get the userId from auth state
      const userId = 'current-user-id';
      await api.unlikeVideo(videoId, userId);
      return videoId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchVideoComments = createAsyncThunk(
  'videos/fetchComments',
  async (videoId: string, { rejectWithValue }) => {
    try {
      return await api.fetchVideoComments(videoId);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const addVideoComment = createAsyncThunk(
  'videos/addComment',
  async ({ videoId, content }: { videoId: string, content: string }, { rejectWithValue }) => {
    try {
      // In a real app, you would get the user info from auth state
      const userId = 'current-user-id';
      const userName = 'Current User';
      const userAvatar = '/images/default-avatar.png';
      
      return await api.addVideoComment(videoId, userId, userName, userAvatar, content);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const videoSlice = createSlice({
  name: 'videos',
  initialState,
  reducers: {
    setCurrentVideo: (state, action: PayloadAction<Video | null>) => {
      state.currentVideo = action.payload;
    },
    clearComments: (state) => {
      state.comments = {};
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch trending videos
      .addCase(fetchTrendingVideos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrendingVideos.fulfilled, (state, action: PayloadAction<Video[]>) => {
        state.loading = false;
        state.trendingVideos = action.payload;
      })
      .addCase(fetchTrendingVideos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Like video
      .addCase(likeVideo.fulfilled, (state, action: PayloadAction<string>) => {
        const videoId = action.payload;
        const video = state.trendingVideos.find(v => v.id === videoId);
        if (video) {
          video.isLiked = true;
          video.likes += 1;
        }
        if (state.currentVideo?.id === videoId) {
          state.currentVideo.isLiked = true;
          state.currentVideo.likes += 1;
        }
      })
      
      // Unlike video
      .addCase(unlikeVideo.fulfilled, (state, action: PayloadAction<string>) => {
        const videoId = action.payload;
        const video = state.trendingVideos.find(v => v.id === videoId);
        if (video) {
          video.isLiked = false;
          video.likes -= 1;
        }
        if (state.currentVideo?.id === videoId) {
          state.currentVideo.isLiked = false;
          state.currentVideo.likes -= 1;
        }
      })
      
      // Fetch comments
      .addCase(fetchVideoComments.pending, (state) => {
        state.commentsLoading = true;
        state.error = null;
      })
      .addCase(fetchVideoComments.fulfilled, (state, action: PayloadAction<VideoComment[]>) => {
        state.commentsLoading = false;
        const videoId = action.payload.length > 0 ? action.payload[0].videoId : '';
        if (videoId) {
          state.comments[videoId] = action.payload;
        }
      })
      .addCase(fetchVideoComments.rejected, (state, action) => {
        state.commentsLoading = false;
        state.error = action.payload as string;
      })
      
      // Add comment
      .addCase(addVideoComment.fulfilled, (state, action: PayloadAction<VideoComment>) => {
        const videoId = action.payload.videoId;
        
        // Initialize the comments array for this video if it doesn't exist
        if (!state.comments[videoId]) {
          state.comments[videoId] = [];
        }
        
        // Add the new comment to the beginning of the array
        state.comments[videoId].unshift(action.payload);
        
        // Increment comment count on the video
        const video = state.trendingVideos.find(v => v.id === videoId);
        if (video) {
          video.comments += 1;
        }
        if (state.currentVideo?.id === videoId) {
          state.currentVideo.comments += 1;
        }
      });
  }
});

// Export actions
export const { setCurrentVideo, clearComments } = videoSlice.actions;

// Export selectors
export const selectTrendingVideos = (state: RootState) => state.videos.trendingVideos;
export const selectCurrentVideo = (state: RootState) => state.videos.currentVideo;
export const selectVideoComments = (state: RootState) => state.videos.comments;
export const selectVideoLoading = (state: RootState) => state.videos.loading;
export const selectCommentsLoading = (state: RootState) => state.videos.commentsLoading;
export const selectVideoError = (state: RootState) => state.videos.error;

export default videoSlice.reducer;
